import React from "react";
import { FlaskConical } from "lucide-react";
import AIStudioTool from "./AIStudioTool";

// ---------------------------------------------------------------------------
// Product-type-aware option catalogues.
// A single master dropdown (`product_type`) drives every downstream select.
// Beverage options were seeded from the Brand Vault 360° 2022 brief; other
// product types were designed to match the same shape so any founder can
// brief a formulation regardless of category.
// ---------------------------------------------------------------------------

const PRODUCT_TYPES = [
  { value: "beverage",      label: "Beverage" },
  { value: "food_snack",    label: "Food & snack" },
  { value: "supplement",    label: "Dietary supplement" },
  { value: "personal_care", label: "Personal care / beauty" },
  { value: "pet",           label: "Pet product" },
  { value: "household",     label: "Household / cleaning" },
  { value: "other",         label: "Other CPG" },
];

const CATEGORY_OPTIONS = {
  beverage: [
    "Energy drink", "Sports drink", "Ready-to-drink coffee", "Cold brew",
    "Ready-to-drink tea", "Kombucha", "Soft drink / soda", "Sparkling water",
    "Still water", "Juice", "Plant-based milk", "Dairy / creamer",
    "Alcoholic (RTD)", "Alcoholic (spirit)", "Functional shot",
    "Meal replacement", "Other",
  ],
  food_snack: [
    "Protein bar", "Snack bar", "Cookie / baked", "Chip / crisp",
    "Popcorn", "Cracker", "Chocolate", "Confection / candy",
    "Frozen novelty", "Cereal / granola", "Nut butter", "Dip / spread",
    "Meal / entrée", "Sauce / condiment", "Baby food", "Other",
  ],
  supplement: [
    "Capsule / softgel", "Tablet", "Powder / drink mix", "Gummy",
    "Chew / tablet", "Liquid tincture", "Effervescent", "Stick pack",
    "Ready-to-drink shot", "Topical patch", "Other",
  ],
  personal_care: [
    "Cleanser / face wash", "Serum", "Moisturiser", "Body cream / lotion",
    "Deodorant", "Toothpaste / oral care", "Shampoo", "Conditioner",
    "Hair styling", "Sun care", "Cosmetic / makeup", "Fragrance",
    "Bar soap", "Feminine care", "Other",
  ],
  pet: [
    "Dry food / kibble", "Wet / canned food", "Treat / biscuit",
    "Chew / dental", "Supplement / powder", "Topical", "Grooming",
    "Freeze-dried", "Broth / stew", "Other",
  ],
  household: [
    "All-purpose cleaner", "Laundry detergent", "Dish soap",
    "Fabric refresher", "Surface wipe", "Air care", "Candle / fragrance",
    "Insect / pest", "Personal wipe", "Other",
  ],
  other: ["Other"],
};

// Trait options are additive across all product types — the founder types
// their picks into a free-text field (comma-separated) so we don't need
// a multi-select. The hint text adapts to the product type.
const TRAIT_HINTS = {
  beverage:      "e.g. organic, non-GMO, kosher, natural, no artificial colours, low-cal",
  food_snack:    "e.g. gluten-free, high-protein, non-GMO, kosher, vegan, keto",
  supplement:    "e.g. clinically-studied doses, third-party tested, non-GMO, vegan capsule",
  personal_care: "e.g. clean, reef-safe, sulfate-free, paraben-free, cruelty-free, vegan",
  pet:           "e.g. grain-free, single-protein, AAFCO complete, freeze-dried raw",
  household:     "e.g. plant-based, biodegradable, EPA Safer Choice, fragrance-free",
  other:         "e.g. any certifications, must-have attributes",
};

const SWEETENER_OPTIONS = [
  "Unsweetened", "Cane sugar", "Honey", "Agave", "Maple syrup",
  "Erythritol", "Monk fruit", "Stevia", "Allulose", "Artificial (sucralose etc.)",
  "Blend", "Other",
];

const PACKAGING_OPTIONS = {
  beverage: [
    "PET bottle", "Aluminum can", "Aluminum bottle", "Glass bottle",
    "Tetra Pak", "HDPE bottle", "Pouch", "Carton", "Custom", "Other",
  ],
  food_snack: [
    "Flow-wrap film", "Stand-up pouch", "Carton / box", "Rigid tray",
    "Jar", "Aluminum can", "Chip bag", "Bar sleeve", "Custom", "Other",
  ],
  supplement: [
    "HDPE bottle", "Glass bottle", "Aluminum can", "Stick pack",
    "Sachet", "Blister pack", "Pouch", "Dropper bottle", "Custom", "Other",
  ],
  personal_care: [
    "PET bottle", "HDPE bottle", "Aluminum bottle", "Tube", "Jar",
    "Airless pump", "Dropper", "Roll-on", "Compact / palette",
    "Bar wrapper", "Custom", "Other",
  ],
  pet: [
    "Stand-up pouch", "Kraft bag", "HDPE bottle", "Aluminum can",
    "Tetra Pak", "Rigid tub", "Custom", "Other",
  ],
  household: [
    "HDPE bottle", "PET bottle", "Trigger spray", "Refill pouch",
    "Aerosol can", "Concentrate sachet", "Cardboard box", "Custom", "Other",
  ],
  other: ["Custom", "Other"],
};

const PACKAGING_SIZES = {
  beverage: [
    "1.5–3 oz shot", "8 oz", "12 oz", "16 oz", "20 oz", "1 L", "Multi-pack",
    "Custom", "Other",
  ],
  food_snack: [
    "Single-serve (~45 g)", "Multi-pack", "Family-size (~200 g+)",
    "Bulk (~500 g+)", "Custom", "Other",
  ],
  supplement: [
    "30-count", "60-count", "90-count", "120-count",
    "12 servings", "30 servings", "Custom", "Other",
  ],
  personal_care: [
    "0.5 fl oz", "1 fl oz", "2 fl oz", "4 fl oz", "8 fl oz", "16 fl oz",
    "Travel", "Custom", "Other",
  ],
  pet: [
    "Single-serve pouch", "3 lb bag", "5 lb bag", "12 lb bag",
    "Multi-pack tray", "Custom", "Other",
  ],
  household: [
    "16 fl oz", "24 fl oz", "32 fl oz", "Refill (64+ fl oz)",
    "Concentrate sachet", "Custom", "Other",
  ],
  other: ["Custom", "Other"],
};

// Consumables get flavor + sweetener + calorie fields. Non-consumables hide
// them (they don't apply to a shampoo or a floor cleaner).
const CONSUMABLE_TYPES = new Set(["beverage", "food_snack", "supplement", "pet"]);

// Helpers to turn a string list into `{ value, label }` options for select.
const asOptions = (list) => list.map(x => ({
  value: x.toLowerCase().replace(/[^a-z0-9]+/g, "-"),
  label: x,
}));

const FIELDS = [
  { key: "product_name", label: "Product name", required: true, wide: true,
    placeholder: "e.g. Morning Boost — Cacao Almond Bar" },

  { key: "product_type", label: "Product type", required: true, type: "select",
    options: PRODUCT_TYPES,
    hint: "Drives every downstream dropdown." },

  { key: "category", label: "Category", required: true, type: "select",
    optionsFor: (form) => asOptions(CATEGORY_OPTIONS[form.product_type] || CATEGORY_OPTIONS.other),
    hint: "The sub-category on the shelf." },

  { key: "target_audience", label: "Target audience", type: "textarea", rows: 2, wide: true,
    placeholder: "e.g. Millennial pre-workout users; wellness moms; keto lifestyle athletes…",
    maxLength: 400 },

  { key: "product_traits", label: "Product traits", type: "textarea", rows: 2, wide: true,
    placeholder: "Comma-separated. Leave blank if none.",
    maxLength: 300 },

  { key: "sweetener_preference", label: "Sweetener preference", type: "select",
    optionsFor: () => [{ value: "", label: "— select or leave blank —" }, ...asOptions(SWEETENER_OPTIONS)],
    visibleWhen: (f) => CONSUMABLE_TYPES.has(f.product_type),
    hint: "Skip for non-consumables." },

  { key: "desired_claims", label: "Marketing claims", type: "textarea", rows: 2, wide: true,
    placeholder: "e.g. high-protein, no added sugar, keto, gluten-free, plant-based",
    hint: "Comma-separated. The AI will show how ingredients back each claim.",
    maxLength: 400 },

  { key: "desired_flavor_profile", label: "Desired flavor profile", type: "textarea", rows: 2, wide: true,
    placeholder: "e.g. Rich chocolate + light almond, tart cherry finish; low bitterness",
    visibleWhen: (f) => CONSUMABLE_TYPES.has(f.product_type),
    maxLength: 400 },

  { key: "calorie_target", label: "Calorie target",
    placeholder: "e.g. under 100 kcal / 12 oz",
    visibleWhen: (f) => CONSUMABLE_TYPES.has(f.product_type) && f.product_type !== "supplement" },

  { key: "serving_size", label: "Serving size",
    placeholder: "e.g. 45 g bar, 12 oz can, 8 g scoop",
    hint: "Anchors macro estimates." },

  { key: "packaging_type", label: "Packaging type", type: "select",
    optionsFor: (form) => [
      { value: "", label: "— select or leave blank —" },
      ...asOptions(PACKAGING_OPTIONS[form.product_type] || PACKAGING_OPTIONS.other),
    ],
    hint: "Options adapt to the product type." },

  { key: "packaging_size", label: "Packaging size", type: "select",
    optionsFor: (form) => [
      { value: "", label: "— select or leave blank —" },
      ...asOptions(PACKAGING_SIZES[form.product_type] || PACKAGING_SIZES.other),
    ] },

  { key: "dietary_restrictions", label: "Dietary restrictions",
    placeholder: "e.g. vegan, gluten-free, non-GMO, kosher",
    visibleWhen: (f) => CONSUMABLE_TYPES.has(f.product_type) },

  { key: "ingredient_wish_list", label: "Ingredient wish list", type: "textarea", rows: 3, wide: true,
    placeholder: "Ingredients you specifically want included, one per line or comma-separated.",
    maxLength: 600 },

  { key: "existing_recipe", label: "Home recipe / existing formula", type: "textarea", rows: 4, wide: true,
    placeholder: "Paste any recipe, ingredient percentages, or R&D notes you already have. Optional but powerful.",
    hint: "Warren will iterate from this rather than starting from scratch.",
    maxLength: 1500 },

  { key: "reference_samples", label: "Reference samples / competitors", type: "textarea", rows: 2, wide: true,
    placeholder: "e.g. Athletic Greens, Poppi Raspberry Rose, Erewhon almond butter bar",
    hint: "Comma-separated brands or product names to benchmark against.",
    maxLength: 400 },
];

const DEMO_INPUTS = [
  {
    label: "Functional beverage · Sparkling adaptogen",
    hint: "Trip / Ghia-adjacent · 25 kcal / can · gut + mood",
    values: {
      product_name: "Field & Fizz — Sparkling Adaptogen Tonic",
      product_type: "beverage", category: "sparkling-water",
      target_audience: "30-45 wellness-forward professionals who quit alcohol and want a ritual",
      product_traits: "functional, adaptogenic, sparkling, low-calorie, non-alcoholic",
      sweetener_preference: "monk-fruit + a whisper of allulose",
      desired_claims: "no added sugar, adaptogenic, non-alcoholic, made with real botanicals",
      desired_flavor_profile: "elderflower · yuzu · rosemary",
      calorie_target: "25 kcal / 12oz",
      serving_size: "12 oz slim can",
      packaging_type: "slim aluminum can",
      packaging_size: "12oz (355ml)",
      dietary_restrictions: "vegan, gluten-free, allergen-friendly",
      ingredient_wish_list: "L-theanine, ashwagandha, lemon balm",
      existing_recipe: "",
      reference_samples: "Ghia, Trip, Recess, De Soi",
    },
  },
  {
    label: "Protein snack · Bar",
    hint: "20g protein · low-sugar · GoMacro-adjacent",
    values: {
      product_name: "Field & Furnace — Almond Espresso Bar",
      product_type: "food_snack", category: "protein-bar",
      target_audience: "high-performing athletes and busy founders needing a clean 3pm bar",
      product_traits: "high-protein, low-added-sugar, plant-based, soft-baked",
      sweetener_preference: "date paste + allulose",
      desired_claims: "20g protein, 5g sugar, plant-based, no soy",
      desired_flavor_profile: "roasted almond · dark espresso · sea salt",
      calorie_target: "220 kcal / bar",
      serving_size: "50g bar",
      packaging_type: "flow-wrap pouch",
      packaging_size: "single 50g",
      dietary_restrictions: "vegan, gluten-free, soy-free",
      ingredient_wish_list: "pea + rice protein, cacao nibs, MCT",
      existing_recipe: "",
      reference_samples: "GoMacro, RXBAR, Aloha",
    },
  },
  {
    label: "Adaptogen supplement · Daily powder",
    hint: "Athletic Greens-adjacent · 12 adaptogens · citrus",
    values: {
      product_name: "Field & Marrow — Daily Adaptogen Blend",
      product_type: "supplement", category: "greens-powder",
      target_audience: "biohackers, morning-routine devotees, 30-45 professionals",
      product_traits: "adaptogen-forward, greens, prebiotic, third-party tested",
      sweetener_preference: "monk-fruit only",
      desired_claims: "12 clinical adaptogens, 30 servings, third-party tested",
      desired_flavor_profile: "yuzu-citrus · light ginger · zero grassy notes",
      calorie_target: "30 kcal / scoop",
      serving_size: "1 scoop (10g)",
      packaging_type: "amber glass jar",
      packaging_size: "300g / 30 servings",
      dietary_restrictions: "vegan, gluten-free, non-GMO",
      ingredient_wish_list: "ashwagandha KSM-66, rhodiola, lion's mane, reishi",
      existing_recipe: "",
      reference_samples: "Athletic Greens, Sakara, Moon Juice",
    },
  },
  {
    label: "Pet treat · Soft-baked",
    hint: "Grain-free chews · Fable-adjacent",
    values: {
      product_name: "Rufflebite — Peanut & Pumpkin Chews",
      product_type: "pet", category: "dog-treats",
      target_audience: "millennial dog parents, urban apartment life, Instagram-active",
      product_traits: "soft-baked, grain-free, limited-ingredient, training-size",
      sweetener_preference: "none — pumpkin purée provides natural sweetness",
      desired_claims: "grain-free, made in Vermont, 5-ingredient, training-size",
      desired_flavor_profile: "roasted peanut · pumpkin · honey trace",
      calorie_target: "8 kcal / chew",
      serving_size: "one 6g chew",
      packaging_type: "stand-up kraft pouch with zip",
      packaging_size: "6oz (170g) resealable",
      dietary_restrictions: "grain-free, no artificial preservatives",
      ingredient_wish_list: "peanut butter, pumpkin purée, oat flour swap for cassava",
      existing_recipe: "",
      reference_samples: "Fable, Beau Life, Bark",
    },
  },
];

const DEFAULTS = {
  product_name: "",
  product_type: "beverage",
  category: "energy-drink",
  target_audience: "",
  product_traits: "",
  sweetener_preference: "",
  desired_claims: "",
  desired_flavor_profile: "",
  calorie_target: "",
  serving_size: "",
  packaging_type: "",
  packaging_size: "",
  dietary_restrictions: "",
  ingredient_wish_list: "",
  existing_recipe: "",
  reference_samples: "",
};

export default function AIStudioFormulation() {
  return (
    <AIStudioTool
      toolSlug="formulation"
      name="Formulation Creator"
      Icon={FlaskConical}
      tagline="Draft a co-manufacturer-ready creative brief for any CPG product — beverages, food, supplements, personal care, pet, household. Warren generates the ingredient deck, macro targets, claim alignment, benchmark competitors, and regulatory flags in about 20 seconds."
      fields={FIELDS}
      endpoint="/ai-studio/formulation/generate"
      defaultValues={DEFAULTS}
      demoInputs={DEMO_INPUTS}
      cta="Draft formulation brief"
      historyLabel="Your formulation briefs"
      textOnly
      formHeading="Brief the formulation"
      footerNote="Briefs typically finish in 15–25 seconds. Always validate with a food scientist before production — the AI can hallucinate dose ranges."
    />
  );
}
